import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'motion/react';
import { FeedbackButton } from './FeedbackButton';

export const FeedbackFloatingButton: React.FC = () => {
  const location = useLocation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipText, setTooltipText] = useState('¿Alguna sugerencia?');

  useEffect(() => {
    const hasSeenTooltip = localStorage.getItem('has_seen_feedback_tooltip');
    if (!hasSeenTooltip) {
      const timer = setTimeout(() => {
        setTooltipText('¿Alguna sugerencia?');
        setShowTooltip(true);

        setTimeout(() => {
          setShowTooltip(false);
          localStorage.setItem('has_seen_feedback_tooltip', 'true');
        }, 4000);
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, []);

  useEffect(() => {
    const handleTrigger = (e: any) => {
      const { text } = e.detail || {};
      if (text) {
        setTooltipText(text);
        setShowTooltip(true);
        setTimeout(() => setShowTooltip(false), 5000);
      }
    };

    window.addEventListener('trigger-feedback-tooltip', handleTrigger);
    return () => window.removeEventListener('trigger-feedback-tooltip', handleTrigger);
  }, []);

  useEffect(() => {
    const checkModals = () => {
      const hasDialog = !!document.querySelector('[role="dialog"]');
      const hasOtherModal = !!document.querySelector('.fixed.inset-0:not(.feedback-modal)');

      setIsModalOpen(hasDialog || hasOtherModal);
    };

    const observer = new MutationObserver(checkModals);
    observer.observe(document.body, { childList: true, subtree: true });
    checkModals();

    return () => observer.disconnect();
  }, []);

  if (isModalOpen) return null;

  // The game screen already exposes this action in its top header, next to chat.
  if (location.pathname.startsWith('/game')) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{
          opacity: 1,
          scale: 1,
          transition: {
            duration: 0.3,
            ease: 'easeOut',
          },
        }}
        exit={{ opacity: 0, scale: 0.8 }}
        className="feedback-floating-anchor flex flex-col items-end gap-2"
      >
        <AnimatePresence>
          {showTooltip && (
            <motion.div
              initial={{ opacity: 0, x: 10, scale: 0.9 }}
              animate={{ opacity: 1, x: 0, scale: 1 }}
              exit={{ opacity: 0, x: 10, scale: 0.9 }}
              className="bg-card-bg border border-grid-line px-4 py-3 rounded-2xl shadow-2xl mb-2 mr-1 max-w-[200px] relative"
            >
              <p className="text-[11px] font-black text-primary-accent uppercase tracking-tight leading-tight">
                {tooltipText}
              </p>
              <div className="absolute bottom-[-6px] right-4 w-3 h-3 bg-card-bg border-r border-b border-grid-line rotate-45" />
            </motion.div>
          )}
        </AnimatePresence>

        <motion.div
          animate={{
            scale: [1, 1.05, 1],
          }}
          transition={{
            duration: 1.2,
            ease: 'easeOut',
            times: [0, 0.5, 1],
            repeat: 0,
          }}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <FeedbackButton
            variant="floating"
            className="group w-10 h-10 rounded-full bg-[#2BB3C0] text-white shadow-[0_3px_8px_rgba(0,0,0,0.12)] flex items-center justify-center transition-transform active:scale-90"
            iconClassName="w-5 h-5"
          />
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};
